// Client-side mirror of the backend slot planner (smartflow-api
// services/planning/slotPlanner.js) so the calendar can preview where a
// dragged work order will land before the drop. The server stays the source
// of truth; this only drives the ghost card and cell feedback.
//
// Works in "minutes since local midnight" space, like the backend.

import { getAppointmentStart } from '@/utils/workOrders';

export const DEFAULT_DURATION_MINUTES = 120;

export const PLACEMENT_STRATEGIES = {
  APPEND: 'append',
  AT: 'at',
  EXACT: 'exact',
};

export const parseHmToMinutes = (hm) => {
  if (typeof hm !== 'string') return null;
  const match = /^(\d{1,2}):(\d{2})$/.exec(hm.trim());
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return null;
  return hours * 60 + minutes;
};

export const minutesToHm = (minutes) => {
  const m = ((Math.round(minutes) % 1440) + 1440) % 1440;
  return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
};

/** Calendar date (YYYY-MM-DD) the calendar files an appointment under. */
export const appointmentDateKey = (iso) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d.toISOString().split('T')[0];
};

/** Minutes since local midnight for an ISO instant. */
export const appointmentLocalMinutes = (iso) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d.getHours() * 60 + d.getMinutes();
};

export const workOrderDuration = (wo) =>
  Number(wo?.appointment_duration_minutes) > 0
    ? Number(wo.appointment_duration_minutes)
    : DEFAULT_DURATION_MINUTES;

/**
 * Busy intervals on `dateStr` for one technician column.
 * @param {Array} workOrders
 * @param {string} dateStr
 * @param {(wo) => boolean} isAssigned  column predicate (matchesInstaller)
 * @param {string} [excludeId]          the work order being dragged
 */
export const busyIntervalsForDay = (workOrders, dateStr, isAssigned, excludeId = null) => {
  const intervals = [];
  for (const wo of workOrders || []) {
    if (!wo || wo.id === excludeId) continue;
    if (wo.status === 'cancelled') continue;
    const start = getAppointmentStart(wo);
    if (!start || appointmentDateKey(start) !== dateStr) continue;
    if (!isAssigned(wo)) continue;
    const startMin = appointmentLocalMinutes(start);
    intervals.push({ startMin, endMin: startMin + workOrderDuration(wo), workOrder: wo });
  }
  return intervals;
};

export const firstFreeSlot = (from, durationMinutes, busyIntervals) => {
  const sorted = [...busyIntervals].sort((a, b) => a.startMin - b.startMin);
  let candidate = from;
  let moved = true;
  while (moved) {
    moved = false;
    for (const interval of sorted) {
      if (candidate < interval.endMin && candidate + durationMinutes > interval.startMin) {
        candidate = interval.endMin;
        moved = true;
      }
    }
  }
  return candidate;
};

const padIntervals = (intervals, pad) =>
  pad ? intervals.map((i) => ({ startMin: i.startMin - pad, endMin: i.endMin + pad })) : intervals;

const mergeIntervals = (intervals) => {
  const sorted = [...intervals].sort((a, b) => a.startMin - b.startMin);
  const merged = [];
  for (const i of sorted) {
    const last = merged[merged.length - 1];
    if (last && i.startMin <= last.endMin) last.endMin = Math.max(last.endMin, i.endMin);
    else merged.push({ startMin: i.startMin, endMin: i.endMin });
  }
  return merged;
};

export const freeMinutesInWorkday = (busyIntervals, workdayStart, workdayEnd) => {
  let busy = 0;
  for (const i of mergeIntervals(busyIntervals)) {
    const s = Math.max(i.startMin, workdayStart);
    const e = Math.min(i.endMin, workdayEnd);
    if (e > s) busy += e - s;
  }
  return Math.max(0, workdayEnd - workdayStart - busy);
};

const lunchInterval = (workTime) => {
  const lunch = workTime?.lunch_break;
  if (!lunch) return null;
  const s = parseHmToMinutes(lunch.start);
  const e = parseHmToMinutes(lunch.end);
  if (s == null || e == null || e <= s) return null;
  return { startMin: s, endMin: e };
};

/**
 * Same semantics as the backend `placeInDay`:
 *  - append: first gap from workday start; must fit → else { ok:false, code:'day_full' }
 *  - at: requested time, snap forward when busy; snapped-and-doesn't-fit → day_full
 *  - exact: requested time, overlap allowed (warning)
 *
 * @returns {{ ok:true, startMin, endMin, moved, warnings:string[], conflicts:Array }
 *        | { ok:false, code:'day_full', freeMinutes, neededMinutes }}
 */
export const placeInDay = ({
  strategy,
  requestedMin = null,
  durationMinutes,
  workTime,
  busyIntervals = [],
}) => {
  const workdayStart = parseHmToMinutes(workTime?.workday_start) ?? 480;
  const workdayEnd = parseHmToMinutes(workTime?.workday_end) ?? 1020;
  const breakMinutes = Number(workTime?.break_minutes_between_jobs) || 0;
  const duration = Number(durationMinutes) > 0 ? Number(durationMinutes) : DEFAULT_DURATION_MINUTES;

  const lunch = lunchInterval(workTime);
  const hardBusy = lunch ? [...busyIntervals, lunch] : busyIntervals;
  const searchBusy = lunch
    ? [...padIntervals(busyIntervals, breakMinutes), lunch]
    : padIntervals(busyIntervals, breakMinutes);

  const conflictsAt = (s, e) => busyIntervals.filter((b) => s < b.endMin && e > b.startMin);
  const outside = (s, e) => s < workdayStart || e > workdayEnd;

  if (strategy === PLACEMENT_STRATEGIES.EXACT) {
    const startMin = requestedMin ?? workdayStart;
    const endMin = startMin + duration;
    const conflicts = conflictsAt(startMin, endMin);
    const warnings = [];
    if (conflicts.length) warnings.push('technician_conflict');
    if (outside(startMin, endMin)) warnings.push('outside_working_hours');
    return { ok: true, startMin, endMin, moved: false, warnings, conflicts };
  }

  const from =
    strategy === PLACEMENT_STRATEGIES.APPEND ? workdayStart : (requestedMin ?? workdayStart);
  const startMin = firstFreeSlot(from, duration, searchBusy);
  const endMin = startMin + duration;
  const moved = startMin !== from;
  const fits = endMin <= workdayEnd;

  if ((strategy === PLACEMENT_STRATEGIES.APPEND || moved) && !fits) {
    return {
      ok: false,
      code: 'day_full',
      freeMinutes: freeMinutesInWorkday(hardBusy, workdayStart, workdayEnd),
      neededMinutes: duration,
    };
  }
  const warnings = [];
  if (outside(startMin, endMin)) warnings.push('outside_working_hours');
  return { ok: true, startMin, endMin, moved, warnings, conflicts: [] };
};

/**
 * Same semantics as the backend `findBlockingHelpers`: which helpers make a
 * placement impossible, and where it lands for the primary alone.
 *
 * @param {Object} params      placeInDay params, with busy time split per person
 * @param {Array}  params.primaryBusy
 * @param {Object} params.helperBusy  { [helperId]: busy intervals }
 * @returns {{ withoutHelpers: Object|null, blockingHelperIds: string[] }}
 *   withoutHelpers is null when the primary alone does not fit either.
 */
export const findBlockingHelpers = ({ primaryBusy = [], helperBusy = {}, ...placement }) => {
  const withoutHelpers = placeInDay({ ...placement, busyIntervals: primaryBusy });
  if (!withoutHelpers.ok) return { withoutHelpers: null, blockingHelperIds: [] };

  const busyHelpers = Object.entries(helperBusy).filter(([, busy]) => busy && busy.length > 0);
  const blockingHelperIds = busyHelpers
    .filter(
      ([, busy]) => !placeInDay({ ...placement, busyIntervals: [...primaryBusy, ...busy] }).ok
    )
    .map(([id]) => id);

  // No single helper blocks, but together they do: all busy helpers count.
  return {
    withoutHelpers,
    blockingHelperIds: blockingHelperIds.length ? blockingHelperIds : busyHelpers.map(([id]) => id),
  };
};

/**
 * Side-by-side lanes for overlapping events in one column (Google Calendar
 * style). Mutates nothing; returns a Map id → { lane, laneCount }.
 * @param {Array<{ id, startMin, endMin }>} items
 */
export const assignLanes = (items) => {
  const result = new Map();
  const sorted = [...items].sort((a, b) => a.startMin - b.startMin || a.endMin - b.endMin);

  // Split into clusters of transitively overlapping items.
  let cluster = [];
  let clusterEnd = -Infinity;
  const flush = () => {
    if (!cluster.length) return;
    const laneEnds = [];
    const lanes = new Map();
    for (const item of cluster) {
      let lane = laneEnds.findIndex((end) => end <= item.startMin);
      if (lane === -1) {
        lane = laneEnds.length;
        laneEnds.push(item.endMin);
      } else {
        laneEnds[lane] = item.endMin;
      }
      lanes.set(item.id, lane);
    }
    for (const item of cluster) {
      result.set(item.id, { lane: lanes.get(item.id), laneCount: laneEnds.length });
    }
    cluster = [];
    clusterEnd = -Infinity;
  };

  for (const item of sorted) {
    if (cluster.length && item.startMin >= clusterEnd) flush();
    cluster.push(item);
    clusterEnd = Math.max(clusterEnd, item.endMin);
  }
  flush();
  return result;
};

/** Human label for a free-minutes amount: "1u 30m", "45m". */
export const formatMinutes = (minutes) => {
  const m = Math.max(0, Math.round(minutes));
  const h = Math.floor(m / 60);
  const rest = m % 60;
  if (h && rest) return `${h}u ${rest}m`;
  if (h) return `${h}u`;
  return `${rest}m`;
};
